import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DockingService } from '../docking.service';
import { take } from 'rxjs/operators';

/**
 * A guard to prevent users from exiting the current view when user monitoring is enabled and active.
 */
@Injectable()
export class ConMonDeactivateGuard implements CanDeactivate<any> {
    /**
     * Creates an instance of ConMonDeactivateGuard.
     *
     * @param {MatDialog} dialog - Angular material dialog used to display a confirmation dialog.
     * @param {DockingService} dockingService - The service used to check if user monitoring is enabled.
     */
    constructor(public readonly dialog: MatDialog, public readonly dockingService: DockingService) {}

    /**
     * Determines whether the user can exit the current view. Will prompt the user with a confirmation dialog if he is
     * currently monitoring a docking.
     *
     * @param {any} component - The guard is applied globally.
     * @param {ActivatedRouteSnapshot} currentRoute - The current route.
     * @param {RouterStateSnapshot} currentState - The current router state.
     * @param {RouterStateSnapshot} [nextState] - The next router state (if any).
     * @returns {Observable<boolean>} - An observable that emits a boolean indicating whether the user can exit the
     * current view. Omits the selection of the confirmation view when closed.
     * Will always emmit true if Connection Monitoring is disabled.
     */
    canDeactivate(
        component: any,
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState?: RouterStateSnapshot
    ): Observable<boolean> {
        let isUserMonitoring = false;
        this.dockingService
            .getUserMonitoring()
            .pipe(take(1))
            .subscribe(userMonitoring => (isUserMonitoring = userMonitoring));
        if (isUserMonitoring) {
            return this.openConfirmationDialog();
        }
        return of(true);
    }

    private openConfirmationDialog(): Observable<boolean> {
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: 'Are you sure you want to stop monitoring the docking? This will pause an ongoing docking.',
            restoreFocus: false
        });

        return dialog.afterClosed();
    }
}
